import { useState, useMemo, useCallback, useEffect } from 'react';
import { BigNumber } from 'ethers';
import styled from 'styled-components/macro';
import { formatEther, parseEther } from 'ethers/lib/utils';
import { useSetChain } from '@web3-onboard/react';
import { useTranslation } from 'react-i18next';

import useWeb3 from '../../contexts/Web3Context';
import useModal from '../../contexts/ModalContext';
import { BaseContainer } from '../Container';
import { PositionInput as BasePositionInput } from '../Input/position';
import { ButtonXL } from '../Button';
import { SlippageInput } from '../SlippageInput';
import { Loader } from '../Loader';
import { DirectionSelector } from './DirectionSelector';
import { LeverageSlider } from './LeverageSlider';
import { EntryInfo } from './EntryInfo';
import { Side } from '../../utils/types';
import { changePosition, openReversePosition } from '../../utils/clearingHouse';
import { ensureTermsAccepted, formatDollarValue, MAX_UINT_256 } from '../../utils/general';
import useMarkets from '../../contexts/MarketsContext';
import useUser from '../../contexts/UserContext';
import useTradeLogic from '../../contexts/TradeLogicContext';
import { useExitPositionInfo } from '../../hooks/useExitPositionInfo';
import { Position } from '../../utils/data';
import { targetChainId } from '../../data/chain';

const ContainerWrapper = styled(BaseContainer)`
  padding: 1.25rem;
  box-sizing: border-box;
  height: auto;
  overflow: visible;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: auto;
  margin-bottom: 0;
`;

const PositionInput = styled(BasePositionInput)`
  margin-top: 1.675rem;
`;

const ButtonWrapper = styled(ButtonXL)`
  margin-top: 2.25rem;
  margin-bottom: 0.75rem;
  width: 100%;
  min-height: 3.25rem;
`;

export function NewPosition() {
  const { provider, address, signer, connect, chainConfig, networkError } = useWeb3();
  const { selectedMarket, refreshMarketInfo } = useMarkets();
  const {
    currentPositions,
    minPositiveOpenNotional,
    refreshGlobalState,
    refreshUserInfo,
    forceNewTraderPosition,
  } = useUser();
  const { setModal, closeModal } = useModal();
  const [, setChain] = useSetChain();
  const { t } = useTranslation();

  const [leverage, setLeverage] = useState(0);
  const [pendingTx, setPendingTx] = useState(false);
  const [quoteInputElem, setQuoteInputElem] = useState<HTMLInputElement>();
  const [quoteInputFocused, setQuoteInputFocused] = useState(false);
  const [baseInputFocused, setBaseInputFocused] = useState(false);

  const currentPosition = useMemo<Position | undefined>(
    () =>
      selectedMarket &&
      currentPositions?.find((pos: Position) => pos.market.id === selectedMarket.id),
    [currentPositions, selectedMarket],
  );

  const {
    proposedAmount,
    minAmount,
    entryPrice,
    liquidationPrice,
    isReversing,
    maxQuoteVal,
    combinedOpenNotional,
    maxLeverage,
    tradingFee,
    insuranceFee,
    priceImpact,
    setIsLong,
    setQuoteInput,
    setSlippage,
    quoteInput,
    isLong,
    slippage,
    newMargin,
  } = useTradeLogic();
  const { minAmount: closeMinAmount, proposedAmount: closeProposedAmount } = useExitPositionInfo({
    currentPosition,
    slippage,
    signerOrProvider: signer ?? provider,
    market: selectedMarket,
    chainConfig,
    user: address,
  });

  const onChangeDirection = (newDirection: Side) => {
    setIsLong(newDirection === Side.Long);
  };

  const onQuoteInputChange = (value: BigNumber | undefined) => {
    setQuoteInput(value);
  };

  const onSlippageChange = (value: number) => {
    setSlippage(value);
  };

  const customError = useMemo(() => {
    if (
      quoteInput &&
      !quoteInput.eq(0) &&
      combinedOpenNotional?.abs().lt(minPositiveOpenNotional?.add(1) ?? MAX_UINT_256)
    )
      return t('Resulting position is below the required minimum size.');
    return undefined;
  }, [combinedOpenNotional, minPositiveOpenNotional, quoteInput, t]);

  const buttonEnabled = useMemo(
    () => Number(quoteInput) > 0 && slippage > 0 && signer,
    [quoteInput, slippage, signer],
  );

  const handleChangePosition = useCallback(async () => {
    setPendingTx(true);
    await ensureTermsAccepted({
      setModal,
      closeModal,
    });
    if (!signer || !provider) {
      connect();
    } else if (selectedMarket?.indexPrice && selectedMarket?.id && chainConfig && address) {
      try {
        if (
          networkError &&
          !(await setChain({
            chainId: targetChainId,
          }))
        ) {
          throw new Error('User refused to switch chains');
        }
        if (isReversing && closeProposedAmount !== undefined) {
          await openReversePosition({
            chainConfig,
            signer,
            marketId: selectedMarket.id,
            closeMinAmount,
            closeProposedAmount,
            openProposedAmount: proposedAmount,
            openMinAmount: minAmount,
            direction: isLong ? Side.Long : Side.Short,
          });
        } else if (!isReversing) {
          await changePosition({
            chainConfig,
            signer,
            marketId: selectedMarket.id,
            proposedAmount,
            direction: isLong ? Side.Long : Side.Short,
            minAmount,
          });
        } else if (closeProposedAmount === undefined) {
          throw new Error('closeProposedAmount is undefined');
        }
        forceNewTraderPosition(selectedMarket.id);
        refreshUserInfo();
        refreshMarketInfo();
        refreshGlobalState();
      } catch (err) {
        console.error(err);
      }
      setPendingTx(false);
    }
  }, [
    chainConfig,
    signer,
    isLong,
    setModal,
    closeModal,
    connect,
    provider,
    selectedMarket,
    minAmount,
    proposedAmount,
    isReversing,
    refreshMarketInfo,
    refreshUserInfo,
    refreshGlobalState,
    closeMinAmount,
    address,
    networkError,
    setChain,
    forceNewTraderPosition,
    closeProposedAmount,
  ]);

  // Force update leverage value
  useEffect(() => {
    if (quoteInput !== undefined && maxQuoteVal && maxQuoteVal.gt(0)) {
      const newLeverage = Number(
        formatEther(quoteInput.mulWei(parseEther(maxLeverage.toFixed(18))).divWei(maxQuoteVal)),
      );
      setLeverage(newLeverage);
    } else if (maxQuoteVal) {
      setLeverage(0);
    }
  }, [quoteInput, maxQuoteVal, maxLeverage]);

  // Force update input value
  useEffect(() => {
    if (
      leverage !== undefined &&
      quoteInputElem &&
      maxQuoteVal &&
      !quoteInputFocused &&
      !baseInputFocused &&
      maxLeverage > 0
    ) {
      const newQuoteValue = Number(
        formatEther(maxQuoteVal.mulWei(parseEther((leverage / maxLeverage).toFixed(18)))),
      ).toFixed(2);
      if (
        quoteInputElem.value !== newQuoteValue &&
        !(quoteInputElem.value === '' && newQuoteValue === '0.00')
      ) {
        quoteInputElem.focus();
        quoteInputElem.value = newQuoteValue;
        quoteInputElem.blur();
      }
    }
  }, [leverage, quoteInputElem, maxQuoteVal, quoteInputFocused, baseInputFocused, maxLeverage]);

  useEffect(() => {
    if (quoteInputElem) {
      quoteInputElem.focus();
      quoteInputElem.value = '';
      quoteInputElem.blur();
    }
  }, [selectedMarket, quoteInputElem]);

  return (
    <ContainerWrapper>
      <ContentContainer>
        <DirectionSelector defaultSelected={Side.Long} onChange={onChangeDirection} />
        <PositionInput
          onChange={onQuoteInputChange}
          setQuoteInputElem={setQuoteInputElem}
          onBaseInputFocusChange={setBaseInputFocused}
          onQuoteInputFocusChange={setQuoteInputFocused}
          headerText={
            <>
              {' '}
              {t('Avail')}: {formatDollarValue(maxQuoteVal, 18, false, 2)}{' '}
            </>
          }
          maxQuoteVal={maxQuoteVal ?? BigNumber.from(0)}
          customError={customError}
        />
        <LeverageSlider
          isLongLabel={
            !quoteInput || quoteInput.eq(0) ? isLong : combinedOpenNotional?.lt(0) ?? isLong
          }
          max={maxLeverage}
          showMarginWarning={
            !!(newMargin && selectedMarket && newMargin.lt(selectedMarket?.minMarginAtCreation)) ||
            (quoteInput !== undefined && newMargin === undefined)
          }
          value={leverage}
          onChange={setLeverage}
        />
        <EntryInfo
          entryPrice={entryPrice}
          priceImpact={priceImpact}
          liquidationPrice={liquidationPrice}
          fee={quoteInput && !quoteInput.eq(0) ? insuranceFee.add(tradingFee).abs() : undefined}
        />
        <SlippageInput options={[0.1, 0.5, 1, 1.5]} onChange={onSlippageChange} max={100} />
        <ButtonWrapper disabled={!buttonEnabled} onClick={handleChangePosition}>
          {pendingTx ? <Loader /> : t('Open position')}
        </ButtonWrapper>
      </ContentContainer>
    </ContainerWrapper>
  );
}
